import React, { useId, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, ChevronDown } from 'lucide-react';
import {
  COMMITMENT_MONTHS,
  DAILY_PRICE_CEIL,
  FIRST_YEAR_TOTAL,
  LOCAL_ZONE_TEXT,
  MAX_PAGES,
  MONTHLY_INCLUDES,
  MONTHLY_PRICE,
  SETUP_INCLUDES,
  SETUP_PRICE,
  VAT_MENTION,
  formatEuro,
} from './offerData';
import { OfferFormTextLink, OfferWhatsAppCta } from './OfferCtaButtons';

interface PriceColumnProps {
  title: string;
  amount: string;
  suffix: string;
  note: string;
  items: readonly string[];
  accentClass: string;
}

const PriceColumn: React.FC<PriceColumnProps> = ({ title, amount, suffix, note, items, accentClass }) => (
  <div className="rounded-card border border-line bg-surface-2 p-5 md:p-6">
    <p className="kicker mb-3">{title}</p>
    <p className="text-fg">
      <span className="price !font-extrabold text-4xl md:text-5xl !tracking-[-0.03em]">{amount}</span>
      <span className="ml-2 text-sm text-fg-muted">{suffix}</span>
    </p>
    <p className="text-xs text-accent-soft mt-2 mb-5">{note}</p>
    <ul className="text-fg-muted text-sm space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${accentClass}`} /> {item}
        </li>
      ))}
    </ul>
  </div>
);

const CommitmentExplainer: React.FC = () => {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <div className="mt-3">
      <button
        type="button"
        className="inline-flex min-h-11 items-center gap-1 text-sm text-accent-soft hover:text-fg transition-colors md:min-h-0"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
      >
        Pourquoi un engagement de {COMMITMENT_MONTHS} mois ?
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            id={contentId}
            key="commitment"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden text-fg-muted text-sm leading-relaxed max-w-xl mx-auto mt-2"
          >
            Il nous permet de proposer la création à {formatEuro(SETUP_PRICE)} au lieu de plusieurs milliers d'euros : le coût est étalé sur la première année. Ensuite, vous êtes libre chaque mois.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

const OfferPricing: React.FC = () => (
  <section id="web-vitrine" className="section-spacing bg-canvas border-t border-line">
    <div className="container-site max-w-5xl">
      <motion.div
        className="text-center mb-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <h2 className="heading-section text-balance mb-3">
          Site internet pour <span className="text-accent-soft">micro-entrepreneurs</span>, artisans et TPE : jusqu'à {MAX_PAGES} pages
        </h2>
        <p className="section-intro mb-2">
          Une seule offre, un prix clair, et tout ce qu'il faut pour être visible et crédible en ligne.
        </p>
        <p className="text-fg-muted text-sm max-w-2xl mx-auto text-balance">{LOCAL_ZONE_TEXT}</p>
      </motion.div>

      <motion.div
        className="card border-accent/40 p-5 md:p-8"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <PriceColumn
            title="À la création"
            amount={formatEuro(SETUP_PRICE)}
            suffix="à la mise en ligne"
            note="Aucun acompte"
            items={SETUP_INCLUDES}
            accentClass="text-accent-soft"
          />
          <PriceColumn
            title="Chaque mois"
            amount={formatEuro(MONTHLY_PRICE)}
            suffix="par mois"
            note={`Soit moins de ${DAILY_PRICE_CEIL}\u00a0€ par jour`}
            items={MONTHLY_INCLUDES}
            accentClass="text-cyan-400"
          />
        </div>

        <div className="text-center">
          <p className="text-fg price text-lg">{formatEuro(FIRST_YEAR_TOTAL)} la première année</p>
          <p className="text-fg-muted text-sm mt-1">
            Engagement {COMMITMENT_MONTHS} mois, puis résiliable chaque mois · Hébergement et nom de domaine à votre nom, non inclus (quelques euros par mois) · {VAT_MENTION}
          </p>
          <CommitmentExplainer />
          <div className="mt-6 flex flex-col items-center gap-3">
            <OfferWhatsAppCta location="pricing" />
            <OfferFormTextLink location="pricing" />
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default OfferPricing;
